<template>
  <p
    v-if="!data"
    class="cp-report-empty"
    :class="{ 'is-error': loadError }"
  >
    {{ loadError || "Carregando…" }}
  </p>
  <div
    v-else
    class="cp-report"
  >
    <!-- Identificação -->
    <div class="cp-team">
      <div>
        <p class="cp-team-name">
          {{ data.team.institution }}
        </p>
        <p class="cp-team-sub">
          {{ data.team.teamName }} · {{ data.team.referenceMonth }}
        </p>
      </div>
    </div>

    <!-- Destaques do dia -->
    <section class="cp-section">
      <h2 class="cp-section-title">
        Precisa de atenção hoje
      </h2>

      <div class="cp-highlights">
        <article
          v-for="item in data.highlights"
          :key="item.key"
          class="cp-card cp-highlight"
          :class="`is-${item.status}`"
        >
          <button
            type="button"
            class="cp-highlight-toggle"
            :aria-expanded="openHighlight === item.key"
            @click="toggleHighlight(item.key)"
          >
            <span class="cp-status">
              <span
                class="cp-status-icon"
                aria-hidden="true"
              >{{ statusIcon(item.status) }}</span>
              {{ item.statusLabel }}
            </span>
            <span class="cp-highlight-main">
              <span class="cp-highlight-total">{{ item.total }}</span>
              <span class="cp-highlight-title">
                {{ item.total === 1 ? "paciente" : "pacientes" }} {{ item.title }}
              </span>
            </span>
            <span class="cp-highlight-desc">{{ item.description }}</span>
          </button>

          <ul
            v-if="openHighlight === item.key"
            class="cp-patient-list"
          >
            <li
              v-for="patient in item.patients"
              :key="patient.patientId"
            >
              <router-link
                class="cp-patient-name"
                :to="{ name: 'myPatientManagement', params: { id: patient.patientId } }"
              >
                {{ patient.name }}
              </router-link>
              <span class="cp-patient-detail">{{ patient.detail }}</span>
            </li>
            <li class="cp-patient-link">
              <router-link :to="{ name: 'myPatients' }">
                Abrir na lista de pacientes →
              </router-link>
            </li>
          </ul>
        </article>
      </div>
    </section>

    <!-- Ocupação e movimento -->
    <section class="cp-section">
      <h2 class="cp-section-title">
        Ocupação e movimento <span class="cp-section-sub">{{ data.team.referenceMonth }}</span>
      </h2>

      <div class="cp-tiles cp-tiles-3">
        <div class="cp-card cp-tile">
          <p class="cp-tile-value">
            {{ occupiedBeds }}
            <span class="cp-tile-of">/ {{ totalBeds }} leitos</span>
          </p>
          <p class="cp-tile-label">
            Internados agora
          </p>
          <div
            class="cp-meter"
            role="img"
            :aria-label="`Ocupação de ${occupancyPct}%`"
          >
            <div
              class="cp-meter-fill"
              :style="{ width: `${occupancyPct}%` }"
            />
          </div>
          <p class="cp-tile-note">
            Ocupação de {{ occupancyPct }}% · adultos {{ data.occupancy.adult.occupied }}/{{ data.occupancy.adult.beds }} ·
            pediatria {{ data.occupancy.pediatric.occupied }}/{{ data.occupancy.pediatric.beds }}
          </p>
        </div>

        <div class="cp-card cp-tile">
          <p class="cp-tile-value">
            {{ data.movement.averageStayDays }} <span class="cp-tile-of">dias</span>
          </p>
          <p class="cp-tile-label">
            Permanência média
          </p>
          <p
            v-if="data.movement.exits > 0"
            class="cp-tile-note"
          >
            {{ data.movement.exits }} {{ data.movement.exits === 1 ? "saída" : "saídas" }} no mês:
            {{ data.movement.deaths }} {{ data.movement.deaths === 1 ? "óbito" : "óbitos" }} ({{ deathsPct }}%) e
            {{ data.movement.discharges }} {{ data.movement.discharges === 1 ? "alta" : "altas" }} ({{ 100 - deathsPct }}%)
          </p>
          <p
            v-else
            class="cp-tile-note"
          >
            Nenhuma saída da internação no mês.
          </p>
        </div>

        <div class="cp-card cp-tile">
          <p class="cp-tile-value">
            {{ data.movement.homeMonitored }}
          </p>
          <p class="cp-tile-label">
            {{ data.movement.homeMonitored === 1 ? "paciente em casa" : "pacientes em casa" }}, acompanhados pela Lila
          </p>
          <p
            v-if="data.movement.homeMonitored > 0"
            class="cp-tile-note"
          >
            {{ data.movement.homeInteracted7Days }} de {{ data.movement.homeMonitored }}
            {{ data.movement.homeInteracted7Days === 1 ? "interagiu" : "interagiram" }} pelo app nos últimos 7 dias
          </p>
          <p
            v-else
            class="cp-tile-note"
          >
            Nenhum paciente em casa no momento.
          </p>
        </div>
      </div>
    </section>

    <!-- Qualidade do cuidado -->
    <section class="cp-section">
      <h2 class="cp-section-title">
        Qualidade do cuidado <span class="cp-section-sub">{{ data.team.referenceMonth }}</span>
      </h2>

      <div class="cp-tiles cp-tiles-3">
        <div
          v-for="item in data.quality"
          :key="item.label"
          class="cp-card cp-tile"
        >
          <p class="cp-tile-value">
            {{ item.value }}
          </p>
          <p class="cp-tile-label">
            {{ item.label }}
          </p>
        </div>
      </div>
    </section>

    <!-- Registro da produção no SUS -->
    <section class="cp-section">
      <h2 class="cp-section-title">
        Registro da produção no SUS
        <span class="cp-section-sub">códigos da Portaria SAES/MS nº 4.288/2026</span>
      </h2>

      <div class="cp-tiles cp-tiles-2 mb-tiles">
        <div class="cp-card cp-tile">
          <span
            class="cp-status"
            :class="`is-${data.registry.situation.tone}`"
          >
            <span aria-hidden="true">{{ statusIcon(data.registry.situation.tone) }}</span> {{ data.registry.situation.label }}
          </span>
          <p class="cp-tile-value">
            <template v-if="data.registry.daysSinceRecord == null">
              —
            </template>
            <template v-else>
              {{ data.registry.daysSinceRecord }}
              <span class="cp-tile-of">{{ data.registry.daysSinceRecord === 1 ? "dia" : "dias" }}</span>
            </template>
          </p>
          <p class="cp-tile-label">
            {{ data.registry.daysSinceRecord == null ? "nenhum registro de produção ainda" : "desde o último registro de produção" }}
          </p>
          <p class="cp-tile-note">
            A política prevê desabilitação após 45 dias sem registro. Mínimo de
            {{ data.registry.minimumPerMonth }} procedimentos/mês:
            {{ currentProduction.value >= data.registry.minimumPerMonth ? "atingido" : `${currentProduction.value} até agora` }}.
          </p>
        </div>
      </div>

      <div class="cp-production">
        <!-- Produção x meta mensal da PNCP -->
        <div class="cp-card cp-chart-card cp-goal-card">
          <header class="cp-chart-header">
            <p class="cp-chart-headline">
              <strong>{{ currentProduction.value }}</strong> procedimentos em
              {{ data.team.referenceMonth.split("/")[0].toLowerCase() }}
            </p>
            <span
              class="cp-status"
              :class="currentProduction.value >= data.productionGoal ? 'is-good' : 'is-warning'"
            >
              <span aria-hidden="true">{{ currentProduction.value >= data.productionGoal ? "✓" : "!" }}</span>
              {{ currentProduction.value >= data.productionGoal ? "Meta atingida" : "Abaixo da meta" }}
            </span>
          </header>

          <div
            class="cp-bars"
            role="img"
            :aria-label="productionAriaLabel"
          >
            <div
              class="cp-goal-line"
              :style="{ bottom: `${pctOfMax(data.productionGoal)}%` }"
            >
              <span>Meta mensal: {{ data.productionGoal }}</span>
            </div>

            <div
              v-for="(item, index) in data.production"
              :key="item.month"
              class="cp-bar-slot"
              @mouseenter="hoverGoalBar = index"
              @mouseleave="hoverGoalBar = null"
            >
              <span
                v-if="hoverGoalBar === index || index === data.production.length - 1"
                class="cp-bar-label"
                :style="{ bottom: `calc(${pctOfMax(item.value)}% + 6px)` }"
              >{{ item.value }}</span>
              <div
                class="cp-bar"
                :class="{ 'is-hover': hoverGoalBar === index }"
                :style="{ height: `${pctOfMax(item.value)}%` }"
              />
            </div>
          </div>
          <div class="cp-bar-axis">
            <span
              v-for="item in data.production"
              :key="item.month"
            >{{ item.month }}</span>
          </div>
        </div>

        <div class="cp-card cp-chart-card">
          <header class="cp-chart-header">
            <p class="cp-chart-headline">
              Produção do mês por procedimento
            </p>
          </header>

          <table class="cp-table">
            <thead>
              <tr>
                <th>Procedimento</th>
                <th>Como é lançado</th>
                <th class="is-number">
                  Qtd.
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in data.procedures"
                :key="item.code"
                :class="{ 'is-muted': !item.value }"
              >
                <td>
                  <span class="cp-code">{{ item.code }}</span>
                  <span class="cp-proc-name">{{ item.name }}</span>
                </td>
                <td>{{ item.howRecorded }}</td>
                <td class="is-number">
                  {{ fmt(item.value) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from "moment";
import { formatDays, getHospitalReport, situationStatus } from "@/services/palliative/networkApi";
import { PNCP_MINIMUM_PER_MONTH, PROCEDURES, formatProcedureCode } from "@/services/palliative/susProductionApi";

const HOW_RECORDED = {
  "BPA-I": "BPA individualizado",
  "BPA-C": "BPA consolidado"
};

const pct = (value) => (value == null ? "—" : `${value}%`);
const day = (date) => moment(date).format("DD/MM");

export default {
  name: "PalliativeCareReport",
  data() {
    return {
      report: null,
      loadError: "",
      openHighlight: null,
      hoverGoalBar: null
    };
  },
  computed: {
    // Resposta da API no formato que o painel já usava.
    data() {
      const r = this.report;
      if (!r) return null;
      const highlight = (key, status, statusLabel, title, description, patients, total = patients.length) => ({
        key,
        status: total ? status : "good",
        statusLabel: total ? statusLabel : "Em dia",
        total,
        title,
        description,
        patients
      });
      return {
        team: {
          institution: r.team.teamName,
          teamName: r.team.municipality || "Equipe assistencial de cuidados paliativos",
          referenceMonth: r.team.referenceMonth
        },
        highlights: [
          highlight(
            "pain", "critical", "Crítico", "com dor não controlada em casa",
            "Em casa, com dor 7 ou mais nos 3 últimos relatos pelo app.",
            r.uncontrolledPainAtHome.map((p) => ({
              patientId: p.patientId,
              name: p.name,
              detail: `Dor ${formatDays(p.pain).replace(",0", "")}/10 · desde ${day(p.date)}`
            }))
          ),
          highlight(
            "dav", "warning", "Atenção", "internados há mais de 72h sem diretivas antecipadas",
            "A conversa sobre as diretivas precisa acontecer cedo na internação.",
            r.withoutDirective72h.map((p) => {
              const days = moment().diff(moment(p.date), "days");
              return { patientId: p.patientId, name: p.name, detail: `Internado há ${days} ${days === 1 ? "dia" : "dias"}` };
            })
          ),
          highlight(
            "silent", "warning", "Atenção", "em casa sem interação há 5 dias",
            "Em acompanhamento pelo app, sem relato de sintomas, questionário semanal nem mensagem no chat recente.",
            r.withoutInteraction5Days.map((p) => ({
              patientId: p.patientId,
              name: p.name,
              detail: p.date ? `Última interação em ${day(p.date)}` : "Nenhuma interação desde a ida para casa"
            }))
          ),
          highlight(
            "matrix", "info", "Em andamento", "com pedido de apoio aguardando a equipe matricial",
            "Pedidos enviados à equipe matricial e ainda não aceitos.",
            [], r.waitingMatrix
          )
        ],
        occupancy: r.occupancy,
        movement: { ...r.movement, averageStayDays: formatDays(r.movement.averageStayDays) },
        quality: [
          { value: pct(r.alertsAnswered24hPct), label: "dos alertas de sintoma em casa respondidos em até 24h" },
          { value: pct(r.deathsAtDesiredPlacePct), label: "dos óbitos no local desejado pelo paciente" },
          { value: pct(r.withCarePlanPct), label: "em cuidados paliativos com plano de cuidados registrado" },
          { value: pct(r.withDirectivesPct), label: "em cuidados paliativos com diretivas antecipadas registradas" },
          { value: pct(r.readmissions30dPct), label: "de reinternação em até 30 dias após a alta" },
          {
            value: r.averageMatrixResponseDays == null ? "—" : `${formatDays(r.averageMatrixResponseDays)} dia`,
            label: "de resposta média da equipe matricial"
          }
        ],
        registry: {
          minimumPerMonth: PNCP_MINIMUM_PER_MONTH,
          daysSinceRecord: r.daysSinceRecord,
          situation: situationStatus(r.situation, { daysSinceRecord: r.daysSinceRecord, production: 0 }, {
            minimumPerMonth: PNCP_MINIMUM_PER_MONTH,
            maxDaysWithoutRecord: 45
          })
        },
        productionGoal: PNCP_MINIMUM_PER_MONTH,
        production: r.production,
        // Catálogo da portaria, com a quantidade confirmada ou exportada no mês.
        procedures: Object.entries(PROCEDURES).map(([code, procedure]) => {
          const total = r.productionByProcedure.find((p) => p.code === code);
          return {
            code: formatProcedureCode(code),
            name: procedure.name,
            howRecorded: HOW_RECORDED[procedure.instrument],
            value: total ? total.total : 0
          };
        }).sort((a, b) => b.value - a.value)
      };
    },
    occupiedBeds() {
      return this.data.occupancy.occupied;
    },
    totalBeds() {
      return this.data.occupancy.beds;
    },
    occupancyPct() {
      return this.data.occupancy.pct || 0;
    },
    deathsPct() {
      const { deaths, exits } = this.data.movement;
      return exits ? Math.round((deaths / exits) * 100) : 0;
    },
    currentProduction() {
      return this.data.production[this.data.production.length - 1] || { month: "", value: 0 };
    },
    chartMax() {
      const values = this.data.production.map((p) => p.value);
      return Math.ceil((Math.max(this.data.productionGoal, ...values) * 1.15) / 10) * 10;
    },
    productionAriaLabel() {
      const values = this.data.production
        .map((p) => `${p.month}: ${p.value}`)
        .join(", ");
      return `Produção mensal contra a meta de ${this.data.productionGoal}. ${values}.`;
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        this.report = await getHospitalReport();
        this.loadError = "";
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o relatório.", error);
        this.loadError = error.message;
      }
    },
    toggleHighlight(key) {
      this.openHighlight = this.openHighlight === key ? null : key;
    },
    statusIcon(status) {
      return { critical: "!!", warning: "!", info: "…", good: "✓" }[status] || "";
    },
    pctOfMax(value) {
      return (value / this.chartMax) * 100;
    },
    fmt(value) {
      return Number(value || 0).toLocaleString("pt-BR");
    }
  }
};
</script>

<style lang="scss" scoped>
$ink: #2b2b3a;
$ink-muted: #6b6b7b;
$primary: #4f3f82;
$primary-soft: #ece9f5;
$grid: #e4e2ea;

$critical: #c0262d;
$critical-soft: #fdecec;
$warning: #9a5b00;
$warning-soft: #fff4e0;
$good: #2e7d32;
$good-soft: #e8f5e9;

.cp-report {
  color: $ink;
}

.cp-card {
  padding: 20px 25px;
  border-radius: 15px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

/* Identificação */
.cp-team {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.cp-team-name {
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: $primary;
}

.cp-team-sub {
  font-size: 14px;
  line-height: 20px;
  color: $ink-muted;
}

.cp-mock-badge {
  padding: 4px 12px;
  border: 1px dashed $ink-muted;
  border-radius: 999px;
  font-size: 12px;
  color: $ink-muted;
  white-space: nowrap;
}

/* Seções */
.cp-section {
  margin-bottom: 32px;
}

.cp-section-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  color: $ink;
}

.cp-section-sub {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 400;
  color: $ink-muted;
}

/* Status (sempre ícone + rótulo, nunca só cor) */
.cp-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;

  &.is-good {
    color: $good;
    background-color: $good-soft;
  }

  &.is-warning {
    color: $warning;
    background-color: $warning-soft;
  }

  &.is-critical {
    color: $critical;
    background-color: $critical-soft;
  }
}

.cp-status-icon {
  font-weight: 800;
}

/* Destaques */
.cp-highlights {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.cp-highlight {
  padding: 0;
  border-top: 4px solid $primary;
  overflow: hidden;

  &.is-critical {
    border-top-color: $critical;
    .cp-status {
      color: $critical;
      background-color: $critical-soft;
    }
  }

  &.is-warning {
    border-top-color: $warning;
    .cp-status {
      color: $warning;
      background-color: $warning-soft;
    }
  }

  &.is-info .cp-status {
    color: $primary;
    background-color: $primary-soft;
  }

  &.is-good {
    border-top-color: $good;
    .cp-status {
      color: $good;
      background-color: $good-soft;
    }
  }
}

.cp-highlight-toggle {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:hover {
    background-color: #faf9fc;
  }
}

.cp-highlight-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.cp-highlight-total {
  font-size: 36px;
  line-height: 40px;
  font-weight: 700;
  color: $ink;
}

.cp-highlight-title {
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
}

.cp-highlight-desc {
  font-size: 13px;
  line-height: 18px;
  color: $ink-muted;
}

.cp-patient-list {
  margin: 0;
  padding: 4px 20px 14px !important;
  border-top: 1px solid $grid;
  list-style: none;

  li {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
    border-bottom: 1px solid $grid;
  }

  li:last-child {
    border-bottom: 0;
  }
}

.cp-patient-name {
  font-size: 14px;
  font-weight: 600;
}

.cp-patient-detail {
  font-size: 12px;
  color: $ink-muted;
}

.cp-patient-link a {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
}

/* Tiles */
.cp-report-empty {
  padding: 24px 0;
  color: #6b6b7b;

  &.is-error {
    color: #db466f;
  }
}

.cp-tiles {
  display: grid;
  gap: 16px;

  &.cp-tiles-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &.cp-tiles-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &.cp-tiles-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  &.mb-tiles {
    margin-bottom: 16px;
  }
}

.cp-tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .cp-status {
    margin-bottom: 6px;
  }
}

.cp-tile-value {
  font-size: 36px;
  line-height: 42px;
  font-weight: 700;
  color: $ink;
}

.cp-tile-of {
  font-size: 16px;
  font-weight: 400;
  color: $ink-muted;
}

.cp-tile-label {
  font-size: 14px;
  line-height: 20px;
  color: $ink;
}

.cp-tile-note {
  margin-top: 6px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

.cp-meter {
  width: 100%;
  height: 8px;
  margin-top: 10px;
  border-radius: 4px;
  background-color: $primary-soft;
  overflow: hidden;
}

.cp-meter-fill {
  height: 100%;
  border-radius: 4px;
  background-color: $primary;
}

/* Produção */
.cp-production {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: 16px;
}

.cp-chart-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 18px;
}

.cp-chart-headline {
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
}

.cp-chart-legend {
  font-size: 12px;
  color: $ink-muted;
}

.cp-bars {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 12px;
  height: 180px;
  margin-top: 24px;
  border-bottom: 1px solid $grid;
}

.cp-goal-card {
  .cp-chart-headline {
    font-weight: 400;

    strong {
      font-size: 22px;
      color: $ink;
    }
  }
}

.cp-goal-line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 2px dashed $ink-muted;
  z-index: 1;
  pointer-events: none;

  span {
    position: absolute;
    right: 0;
    top: -20px;
    padding: 0 4px;
    font-size: 11px;
    color: $ink-muted;
    background-color: #ffffff;
  }
}

.cp-chart-note {
  margin-top: 14px !important;
  font-size: 12px;
  line-height: 16px;
  color: $ink-muted;
}

.cp-bar-slot {
  position: relative;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
}

.cp-bar {
  width: 60%;
  max-width: 36px;
  border-radius: 4px 4px 0 0;
  background-color: $primary;
  transition: opacity 0.15s ease-in-out;

  &.is-hover {
    opacity: 0.8;
  }
}

.cp-bar-label {
  position: absolute;
  font-size: 12px;
  font-weight: 700;
  color: $ink;
}

.cp-bar-axis {
  display: flex;
  gap: 12px;
  margin-top: 6px;

  span {
    flex: 1;
    font-size: 12px;
    text-align: center;
    color: $ink-muted;
  }
}

/* Tabela de procedimentos */
.cp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    padding: 0 8px 8px 0;
    border-bottom: 1px solid $grid;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    color: $ink-muted;
  }

  td {
    padding: 10px 8px 10px 0;
    border-bottom: 1px solid $grid;
    vertical-align: top;
    color: $ink;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .is-number {
    text-align: right;
    white-space: nowrap;
    font-weight: 700;
  }

  tr.is-muted td {
    color: $ink-muted;
  }
}

.cp-code {
  display: block;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: $ink-muted;
}

.cp-proc-name {
  display: block;
  font-weight: 600;
}

.cp-table-note {
  font-size: 12px;
  font-weight: 400;
}

@media (max-width: 1200px) {
  .cp-highlights,
  .cp-tiles.cp-tiles-4,
  .cp-tiles.cp-tiles-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cp-production {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 700px) {
  .cp-highlights,
  .cp-tiles.cp-tiles-2,
  .cp-tiles.cp-tiles-3,
  .cp-tiles.cp-tiles-4 {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
